import { Component, OnInit, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { StatusPipe } from '@src/app/shared/pipes/status.pipe';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { CountryTransformPipe } from '@src/app/shared/pipes/country.pipe';
import { UsersService, SetPasswordRequest, DisableMfaRequest, UserDetailsResponse } from '@src/app/shared/backend/clients';
import { UserRolesService } from '@src/app/shared/services/userRoles.service';
import { Dialog } from 'primeng/dialog';
import { passwordRegexValidator } from '@src/app/shared/validators/password.validator';
import { confirmPasswordValidator } from '@src/app/shared/validators/confirmPassword.validator';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-view-user',
    templateUrl: './view-user.component.html',
    styleUrl: './view-user.component.scss',
    host: {
        class: 'app-view-user'
    },
    providers: [StatusPipe, ConfirmationService, CountryTransformPipe],
    standalone: false
})
export class ViewUserComponent extends BaseViewComponent implements OnInit, OnDestroy {
  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  displayChangePasswordModalbox: boolean = false;
  @ViewChild('changePasswordDialog') changePasswordDialog!: Dialog;

  allowModifyUser: boolean = false;

  delete: boolean = false;
  userId: string = '';
  userUsersData: any;
  retailersAssignedTo: any;
  marketsAssignedTo: any;
  user: UserDetailsResponse = {
    id: ''
  };

  canViewRetailers: boolean = false;
  canViewMarkets: boolean = false;

  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  userHasMfaEnabled: boolean = false;
  displayMfaUnenrollModalbox: boolean = false;

  stateOptions: any[];
  stateInitialValue: any;
  stateNewValue: any;
  sanitizedConfirmMessage: SafeHtml | undefined;

  public updatePasswordForm: FormGroup = this.fb.group({
    password: [null, [Validators.required, passwordRegexValidator()]],
    confirmPassword: [null, [Validators.required, confirmPasswordValidator('password')]]
  });

  constructor(
    private fb: FormBuilder,
    private toasterService: ToasterService,
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    public userRoleService: UserRolesService,
    private translate: TranslateService,
    private usersService: UsersService,
    private confirmationService: ConfirmationService,
    private changeDetector: ChangeDetectorRef) {
    super();
    this.updateStateOptions();
  }

  ngOnInit() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(() => {
        this.updateStateOptions();
      });

    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.userId = params['userId'];

      if (this.userId) {
        await this.get();
      }
    })
  }

  private updateStateOptions(): void {
    this.stateOptions = [
      {
        label: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        value: true
      },
      {
        label: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        value: false
      }
    ];
  }

  initStateSwitch() {
    this.stateInitialValue = this.user.isActive;

    const selectedLabel = this.stateOptions.find(option => option.value === this.stateInitialValue)?.label.toLowerCase();
    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);
  }

  onStateChange(event: any) {
    this.stateNewValue = event.value;
    const selectedLabel = this.stateOptions.find(option => option.value === this.stateNewValue)?.label.toLowerCase();

    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);

    // State update confirmation dialog
    this.confirmationService.confirm({
      key: 'confirm-state-change-dialog',
      header: this.translate.instant('MODALBOXES.TITLE_CONFIRM_STATE_CHANGE')
    });
  }

  override async get() {
    this.getChild(this.userId);
  }

  override async getChild(id: any) {
    try {
      this.userId = id;

      this.user = await firstValueFrom(this.usersService.apiUsersDetailsIdGet(this.userId));

      this.initStateSwitch();
      this.retailersAssignedTo = this.user?.retailers;
      this.marketsAssignedTo = this.user?.markets;
      this.userHasMfaEnabled = this.user?.isMfaEnabled || false;

      this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
      this.allowModifyUser = await this.permissionService.hasActionPermissions(Actions.modify_users);
      this.canViewMarkets = await this.permissionService.hasActionPermissions(Actions.view_markets);
      this.canViewRetailers = await this.permissionService.hasActionPermissions(Actions.view_retailers);

    } catch (error) {
      console.log('Error fetching user data:', error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  onConfirmUnenrollMfa() {
    this.displayMfaUnenrollModalbox = true;
  }

  async onUnenrollMfa() {
    try {

      if (!this.user?.username) {
        this.toasterService.show('error', 'Invalid user data', 'Username is required.');
        return;
      }

      this.displayMfaUnenrollModalbox = false;

      const payload: DisableMfaRequest = {
        username: this.user?.username
      };

      await firstValueFrom(this.usersService.apiUsersMfaDisablePost(payload));
      this.userHasMfaEnabled = false;

      const toastTitle = this.translate.instant('OTP.UNENROLL_SUCCESS_TITLE');
      const toastMessage = this.translate.instant('OTP.UNENROLL_SUCCESS_MESSAGE');
      this.toasterService.show('success', toastTitle, toastMessage);

    } catch (err) {
      console.error('Unenroll MFA failed:', err);
      this.toasterService.show('error',
        this.translate.instant('OTP.UNENROLL_FAILED_TITLE'),
        this.translate.instant('OTP.UNENROLL_FAILED_MESSAGE')
      );
    }
  }

  goToEditUser() {
    this.router.navigate([`/users/${this.userId}/edit`]);
  }


  onOpenChangePasswordModalbox() {
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.displayChangePasswordModalbox = true;
  }

  onCancelChangePassword() {
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.displayChangePasswordModalbox = false;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  async onSubmitChangePasswordForm() {
    if (this.updatePasswordForm.valid) {
      const payload: SetPasswordRequest = {
        password: this.updatePasswordForm.controls['password'].value
      }

      const updatePassword = await firstValueFrom(this.usersService.apiUsersIdSetPasswordPut(this.userId, payload));

      if (!updatePassword) {
        this.displayChangePasswordModalbox = false;

        const toastTile = this.translate.instant('YOUR_ACCOUNT.TOAST_PASSWORD_CHANGE_TITLE_OK');
        const toastMessage = this.translate.instant('YOUR_ACCOUNT.TOAST_PASSWORD_CHANGE_DESCRIPTION_OK');
        this.toasterService.show('success', toastTile, toastMessage);
      } else {
        const toastTile = this.translate.instant('YOUR_ACCOUNT.TOAST_PASSWORD_CHANGE_TITLE_KO');
        const toastMessage = this.translate.instant('YOUR_ACCOUNT.TOAST_PASSWORD_CHANGE_DESCRIPTION_KO');
        this.toasterService.show('error', toastTile, toastMessage);
      }

      this.updatePasswordForm.reset();
      this.displayChangePasswordModalbox = false;
    }
  }

  onConfirmStateChange() {
    if (!this.stateNewValue) {
      this.usersService.apiUsersIdDeactivatePut(this.userId).subscribe({
        next: async () => {
          await this.getChild(this.userId);
        },
        error: (error) => {
          console.error('Failed to deactivate user', error);
        }
      });
    } else if (this.stateNewValue) {
      this.usersService.apiUsersIdActivatePut(this.userId).subscribe({
        next: async () => {
          await this.getChild(this.userId);
        },
        error: (error) => {
          console.error('Failed to activate user', error);
        }
      });
    }
    this.confirmationService.close();
  }

  onCancelStateChange() {
    // Revert state change
    const nonMatchingOption = this.stateOptions.find(option => option.value !== this.stateInitialValue).value;
    this.stateInitialValue = nonMatchingOption;
    this.confirmationService.close();
  }

  async deleteUser(doDelete: boolean): Promise<void> {
    if (doDelete) {
      await firstValueFrom(this.usersService.apiUsersDeleteIdDelete(this.userId));

      this.router.navigate([`/users`]);
    }
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
